import React from 'react';
import Autocomplete, { AutocompleteRenderGroupParams, createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Avatar, makeStyles, TextField, Theme } from '@material-ui/core';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { ReferralStatus } from '../types/types';
import { StyledText } from './StyledText';
import StatusChip from './StatusChip';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  contactSelectOption: {
    display: 'flex',
    width: '100%',
    alignItems: 'center'
  },
  contactSelectOptionDetails: {
    margin: '0 15px',
    overflow: 'hidden'
  },
  contactTopLine: {
    display: 'flex',
    width: 'auto',
    alignItems: 'center'
  },
  matchPercent: {
    color: theme.palette.primary.main
  },
  matchSpan: {
    padding: '0px 10px',
    fontWeight: theme.typography.fontWeightLight,
    fontSize: 16
  }
}));

interface ReferralOption {
  investorProfileId: number
  profileImage?: string
  matchScore: number
  companyName: string
  contactName: string
  referralStatus?: ReferralStatus
  email?: string
}
interface ContactSelectProps {
  onChange: any
  onBlur: any
  id: string
  label: string
  value: any
  menuItems: ReferralOption[]
  showCompany?: boolean
}


function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number)
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const SIZE = 80;

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={320}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='div'
          itemSize={() => SIZE}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const renderGroup = (params: AutocompleteRenderGroupParams) => [
  params.children
];

const ContactSelect: React.FC<ContactSelectProps> = ({
  onChange,
  onBlur,
  id,
  value,
  label,
  menuItems,
  showCompany = true
}) => {
  const styles = useStyles();

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option.email + ',' + option.contactName + ',' + option.companyName
  });

  return (
    <Autocomplete
      id={id}
      disableListWrap
      options={menuItems}
      getOptionLabel={(option: ReferralOption) => option.contactName}
      getOptionSelected={(option: any, val: any) => (val.investorProfileId === option.investorProfileId)}
      renderGroup={renderGroup}
      ListboxComponent={ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      renderOption={(option) => (
        <div className={styles.contactSelectOption} key={option.investorProfileId}>
          <Avatar src={option.profileImage} />
          <div className={styles.contactSelectOptionDetails}>
            <div className={styles.contactTopLine}>
              <StyledText>{option.contactName}</StyledText>
              { !option.referralStatus ? (
                <StyledText noWrap={false} variant='subtitle2' align='center'>
                  <span className={styles.matchSpan}>-</span>
                  <span className={styles.matchPercent}>{`${option.matchScore}%`}</span>
                </StyledText>
              ) : null }
            </div>
            {showCompany && <StyledText>{option.companyName}</StyledText>}
            {
              option.email ? <StyledText noWrap style={{ wordBreak: 'break-all', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{option.email}</StyledText> : null
            }
          </div>
          {option.referralStatus && <StatusChip status={option.referralStatus} />}
        </div>
      )}
      multiple
      fullWidth
      filterOptions={filterOptions}
      renderInput={(params: any) => {
        const dis = false;
        return (
          <TextField
            id={id}
            fullWidth
            disabled={dis}
            inputProps={params.inputProps}
            InputLabelProps={params.InputLabelProps}
            label={label}
            variant='outlined'
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
          />
        );
      }}
    />
  );
};

export default ContactSelect;
